/**
 * Odisseia Audio Engine
 * Web Audio API wrapper for game SFX with volume control.
 * All sounds at 30% volume by default (spec requirement).
 * Respects user preference - no sound if system muted.
 */

const AUDIO_BASE = '/audio/odisseia/';
const DEFAULT_VOLUME = 0.3;

type SoundKey =
  | 'xp-gain'
  | 'mission-complete'
  | 'badge-unlock'
  | 'title-ceremony'
  | 'noble-ceremony'
  | 'streak-milestone'
  | 'streak-freeze'
  | 'level-up'
  | 'claim-button-ambient';

// Cache loaded audio buffers to avoid re-fetching
const audioCache = new Map<SoundKey, HTMLAudioElement>();

// Currently playing looped audio
let activeLoop: HTMLAudioElement | null = null;

function getAudio(key: SoundKey): HTMLAudioElement {
  let audio = audioCache.get(key);
  if (!audio) {
    audio = new Audio(`${AUDIO_BASE}${key}.mp3`);
    audio.preload = 'auto';
    audioCache.set(key, audio);
  }
  return audio;
}

/**
 * Play a one-shot sound effect.
 */
export function playSound(key: SoundKey, volume = DEFAULT_VOLUME): void {
  try {
    const audio = getAudio(key);
    audio.volume = Math.max(0, Math.min(1, volume));
    audio.currentTime = 0;
    audio.loop = false;
    audio.play().catch(() => {
      // Autoplay blocked - user hasn't interacted yet, silently fail
    });
  } catch {
    // Audio not supported or file not found
  }
}

/**
 * Start a looping sound (e.g., claim button ambient).
 * Only one loop can play at a time.
 */
export function startLoop(key: SoundKey, volume = DEFAULT_VOLUME * 0.6): void {
  stopLoop();
  try {
    const audio = getAudio(key);
    audio.volume = Math.max(0, Math.min(1, volume));
    audio.currentTime = 0;
    audio.loop = true;
    audio.play().catch(() => {});
    activeLoop = audio;
  } catch {
    // Silently fail
  }
}

/**
 * Stop the currently playing loop.
 */
export function stopLoop(): void {
  if (activeLoop) {
    activeLoop.pause();
    activeLoop.currentTime = 0;
    activeLoop.loop = false;
    activeLoop = null;
  }
}

/**
 * Preload sounds to avoid delay on first play.
 * Call this after user interaction (e.g., page mount with click).
 */
export function preloadSounds(keys: SoundKey[]): void {
  keys.forEach((key) => {
    const audio = getAudio(key);
    // Trigger load without playing
    audio.load();
  });
}

/**
 * Preload ceremony-related sounds for instant playback.
 */
export function preloadCeremonySounds(): void {
  preloadSounds(['title-ceremony', 'noble-ceremony']);
}
